"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useJobStatus = void 0;
const react_1 = require("react");
const jobStatus_1 = require("../../context/jobStatus");
/**
 * Custom hook for managing job status modal state and interactions
 *
 * Supports both local state management (for backward compatibility) and global modal
 * (for modal-to-modal transitions). Use `useGlobalModal: true` when you need the modal
 * to persist across component unmounts.
 *
 * @example
 * ```tsx
 * // Local modal (existing behavior)
 * const jobStatus = useJobStatus({
 *   autoClose: true,
 *   autoCloseDelay: 2000
 * });
 *
 * // Global modal (for modal-to-modal transitions)
 * const jobStatus = useJobStatus({
 *   useGlobalModal: true,
 *   autoClose: false
 * });
 * ```
 */
const useJobStatus = (props = {}) => {
    const { title = 'Processing Operation', description, autoClose = false, autoCloseDelay = 2000, closeable = false, showDetails = false, useGlobalModal = false, onComplete, onError, showItemCount = true, customCompleteMessage, customFailedMessage, customActiveMessage, } = props;
    // Local state for backward compatibility
    const [localState, setLocalState] = (0, react_1.useState)({
        isModalOpen: false,
        currentJobId: null,
        modalTitle: 'Processing Operation',
        modalDescription: undefined,
        lastJobResult: null,
        lastError: null,
    });
    // Global context (optional)
    const context = (0, react_1.useContext)(jobStatus_1.JobStatusContext);
    // Validate global modal usage
    if (useGlobalModal && !context) {
        throw new Error('useJobStatus with useGlobalModal: true must be used within a JobStatusProvider');
    }
    // Choose state and actions based on mode
    const isGlobalMode = useGlobalModal && context;
    const { state: globalState, actions: globalActions } = context || {};
    /**
     * Local state management functions
     */
    const localStartJob = (0, react_1.useCallback)(async (jobId, overrideTitle, overrideDescription) => {
        setLocalState((prev) => ({
            ...prev,
            isModalOpen: true,
            currentJobId: jobId,
            modalTitle: overrideTitle || title,
            modalDescription: overrideDescription || description,
            lastJobResult: null,
            lastError: null,
        }));
    }, [title, description]);
    const localCloseModal = (0, react_1.useCallback)(() => {
        setLocalState((prev) => ({
            ...prev,
            isModalOpen: false,
            currentJobId: null,
            modalTitle: 'Processing Operation',
            modalDescription: undefined,
        }));
    }, []);
    const localHandleJobComplete = (0, react_1.useCallback)((jobStatus) => {
        setLocalState((prev) => ({
            ...prev,
            lastJobResult: jobStatus,
            lastError: null,
        }));
        if (!autoClose) {
            return;
        }
        setTimeout(() => {
            setLocalState((prev) => ({
                ...prev,
                isModalOpen: false,
                currentJobId: null,
                modalTitle: 'Processing Operation',
                modalDescription: undefined,
            }));
        }, autoCloseDelay + 100);
    }, [autoClose, autoCloseDelay]);
    const localHandleJobError = (0, react_1.useCallback)((error) => {
        setLocalState((prev) => ({
            ...prev,
            lastError: error,
            lastJobResult: null,
        }));
    }, []);
    const localClearResults = (0, react_1.useCallback)(() => {
        setLocalState((prev) => ({
            ...prev,
            lastJobResult: null,
            lastError: null,
        }));
    }, []);
    /**
     * Global state management functions
     */
    const globalStartJob = (0, react_1.useCallback)(async (jobId, overrideTitle, overrideDescription) => {
        if (!globalActions)
            return;
        const startJobProps = {
            jobId,
            title: overrideTitle || title,
            description: overrideDescription || description,
            autoClose,
            autoCloseDelay,
            closeable,
            showDetails,
            onComplete,
            onError,
            showItemCount,
            customCompleteMessage,
            customFailedMessage,
            customActiveMessage,
        };
        await globalActions.startJob(startJobProps);
    }, [globalActions, title, description, autoClose, autoCloseDelay, closeable, showDetails, onComplete, onError, showItemCount, customCompleteMessage, customFailedMessage, customActiveMessage]);
    const globalCloseModal = (0, react_1.useCallback)(() => {
        if (!globalActions)
            return;
        globalActions.closeModal();
    }, [globalActions]);
    const globalHandleJobComplete = (0, react_1.useCallback)((jobStatus) => {
        if (!globalActions)
            return;
        globalActions.handleJobComplete(jobStatus);
    }, [globalActions]);
    const globalHandleJobError = (0, react_1.useCallback)((error) => {
        if (!globalActions)
            return;
        globalActions.handleJobError(error);
    }, [globalActions]);
    const globalClearResults = (0, react_1.useCallback)(() => {
        if (!globalActions)
            return;
        globalActions.clearResults();
    }, [globalActions]);
    // Select the appropriate functions and state
    const startJob = isGlobalMode ? globalStartJob : localStartJob;
    const closeModal = isGlobalMode ? globalCloseModal : localCloseModal;
    const handleJobComplete = isGlobalMode ? globalHandleJobComplete : localHandleJobComplete;
    const handleJobError = isGlobalMode ? globalHandleJobError : localHandleJobError;
    const clearResults = isGlobalMode ? globalClearResults : localClearResults;
    // Transform state based on mode
    const state = isGlobalMode && globalState
        ? {
            isModalOpen: globalState.isModalOpen,
            currentJobId: globalState.currentJobId,
            modalTitle: globalState.modalConfig.title,
            modalDescription: globalState.modalConfig.description,
            lastJobResult: globalState.lastJobResult,
            lastError: globalState.lastError,
        }
        : localState;
    const isTrackingJob = state.isModalOpen && state.currentJobId !== null;
    return {
        state,
        startJob,
        closeModal,
        handleJobComplete,
        handleJobError,
        isTrackingJob,
        clearResults,
        showItemCount,
    };
};
exports.useJobStatus = useJobStatus;
